import { type EnterpriseSummary } from "@mg/schemas/src/christo/catalyst";
import { EnterpriseProfileType } from "@mg/schemas/src/commons";
import { Check } from "@phosphor-icons/react";
import { Dialog, Button, TextField, Flex, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../contexts/ui";
import {
  enterpriseSlugProblem,
  findEnterpriseIdentityConflict,
  isValidEnterpriseSlug,
} from "../utils/enterpriseDuplicate";
import { useAppSelector } from "../utils/hooks";
import { errorMessage } from "../utils/http";
import {
  useEnterprises,
  useUpdateEnterprise,
} from "../utils/queries/enterprises";
import { BaseDialogProps } from "../utils/types/dialog";

export type RenameEnterpriseDialogProps = BaseDialogProps & {
  enterprise: EnterpriseSummary | null;
};

export function RenameEnterpriseDialog({
  open,
  onOpenChange,
  enterprise,
}: RenameEnterpriseDialogProps) {
  const { t } = useTranslation("ui");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const updateEnterpriseMutation = useUpdateEnterprise();
  const enterprisesQuery = useEnterprises();
  const { notify } = useUI();
  const user = useAppSelector((state) => state.auth.value);

  useEffect(() => {
    if (!open || !enterprise) return;
    setName(enterprise.name);
    setSlug(enterprise.slug ?? "");
    setServerError(null);
  }, [open, enterprise]);

  if (user?.role !== EnterpriseProfileType.MEANINGFUL_GIGS) {
    return null;
  }

  const trimmedName = name.trim();
  const trimmedSlug = slug.trim();
  const originalName = enterprise?.name ?? "";
  const originalSlug = enterprise?.slug ?? "";
  const nameChanged = trimmedName !== originalName;
  const slugChanged = trimmedSlug !== originalSlug;
  // why: `handleSave` PATCHes only the fields that changed and christo
  // `updateEnterprise` mirrors that — it calls `assertIdentityAvailable` with
  // `updates.name` / `updates.slug`, so an omitted field is never checked.
  // Enterprise.name has no unique index (prod predates this guard and still
  // holds duplicates), so checking an untouched name here refused a rename the
  // server would have accepted; and because the helper reports name before
  // slug, an untouched duplicate name hid the real conflict on the slug the
  // user was editing.
  // why-not: narrowing `slug` the same way is inert — the helper returns
  // `name` before it looks at the slug, so the unchanged slug can only ever add
  // a "slug" result; that reaches only `slugError`, which is already null when
  // `!slugChanged`, so `nameError`, `slugError` and `canSave` cannot differ and
  // no test could pin it.
  const conflict = findEnterpriseIdentityConflict(enterprisesQuery.data ?? [], {
    name: nameChanged ? trimmedName : undefined,
    slug: trimmedSlug,
    excludeId: enterprise?._id,
  });
  const slugProblem = enterpriseSlugProblem(trimmedSlug);
  const nameError =
    conflict === "name"
      ? t("createEnterpriseDialog.nameTaken", { name: trimmedName })
      : null;
  const slugError = !slugChanged
    ? null
    : conflict === "slug"
      ? t("createEnterpriseDialog.slugTaken", { slug: trimmedSlug })
      : slugProblem === "length"
        ? t("settings.enterpriseSettingsView.slugMaxLength")
        : slugProblem === "charset" || trimmedSlug.length === 0
          ? t(
              "settings.enterpriseSettingsView.useLowercaseLettersNumbersHyphens",
            )
          : null;
  const canSave =
    enterprise != null &&
    trimmedName.length > 0 &&
    (nameChanged || slugChanged) &&
    !nameError &&
    !slugError &&
    !updateEnterpriseMutation.isPending;

  const handleSave = async () => {
    if (!canSave || !enterprise) return;
    setServerError(null);
    const updates: { name?: string; slug?: string } = {};
    if (nameChanged) updates.name = trimmedName;
    if (slugChanged && isValidEnterpriseSlug(trimmedSlug)) {
      updates.slug = trimmedSlug;
    }
    try {
      await updateEnterpriseMutation.mutateAsync({
        enterpriseId: enterprise._id,
        ...updates,
      });
      notify({
        variant: "success",
        title: t("renameEnterpriseDialog.title"),
        message: trimmedName,
      });
      onOpenChange?.(false);
    } catch (error) {
      const message = (await errorMessage(error)).trim();
      setServerError(message || t("renameEnterpriseDialog.failedRename"));
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{ maxWidth: 450 }}
        data-testid="rename-enterprise-dialog"
        aria-labelledby="rename-enterprise-dialog-title"
      >
        <Dialog.Title id="rename-enterprise-dialog-title">
          {t("renameEnterpriseDialog.title")}
        </Dialog.Title>
        <form
          data-testid="rename-enterprise-form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <Flex direction="column" gap="3">
            <label htmlFor="rename-enterprise-name">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("renameEnterpriseDialog.name")}
              </Text>
              <TextField.Root
                id="rename-enterprise-name"
                name="rename-enterprise-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="rename-enterprise-name-input"
                aria-invalid={nameError ? true : undefined}
                aria-describedby={
                  nameError ? "rename-enterprise-name-error" : undefined
                }
              />
              {nameError ? (
                <Text
                  id="rename-enterprise-name-error"
                  data-testid="rename-enterprise-name-error"
                  size="1"
                  color="red"
                  mt="1"
                >
                  {nameError}
                </Text>
              ) : null}
            </label>
            <label htmlFor="rename-enterprise-slug">
              <Text as="div" size="2" mb="1" weight="bold">
                {t("renameEnterpriseDialog.slug")}
              </Text>
              <TextField.Root
                id="rename-enterprise-slug"
                name="rename-enterprise-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                data-testid="rename-enterprise-slug-input"
                aria-label={t("renameEnterpriseDialog.slug")}
                aria-invalid={slugError ? true : undefined}
                aria-describedby={
                  slugError ? "rename-enterprise-slug-error" : undefined
                }
              />
              {slugError ? (
                <Text
                  id="rename-enterprise-slug-error"
                  data-testid="rename-enterprise-slug-error"
                  size="1"
                  color="red"
                  mt="1"
                >
                  {slugError}
                </Text>
              ) : null}
            </label>
            {serverError ? (
              <Text
                role="alert"
                size="2"
                color="red"
                data-testid="rename-enterprise-error"
              >
                {serverError}
              </Text>
            ) : null}
          </Flex>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button
                type="button"
                variant="soft"
                color="gray"
                data-testid="rename-enterprise-cancel-button"
              >
                {t("cancel")}
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              disabled={!canSave}
              loading={updateEnterpriseMutation.isPending}
              data-testid="rename-enterprise-save-button"
            >
              <Check />
              {t("save")}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
